import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import type { Drone, Settings } from '@tonist/shared';
import { useDrones } from '../../store/dronesStore';
import { useSettings } from '../../store/settingsStore';
import { FamilyDot } from '../FamilyDot';
import { SLOT_LABELS } from '../../music/assignment';
import { usePreview } from '../../lib/preview';

const ASSIGNMENTS: Array<[Settings['assignment'], string]> = [['single', 'Single'], ['ring', 'By ring'], ['direction', 'By direction']];

export function DroneSelector({ onClose }: { onClose: () => void }) {
  const presets = useDrones((s) => s.presets);
  const mine = useDrones((s) => s.mine);
  const assignment = useSettings((s) => s.assignment);
  const droneIds = useSettings((s) => s.droneIds);
  const set = useSettings((s) => s.set);
  const setDroneSlot = useSettings((s) => s.setDroneSlot);
  const [q, setQ] = useState('');
  const [slot, setSlot] = useState(0);
  const slots = SLOT_LABELS[assignment];
  const activeSlot = Math.min(slot, slots.length - 1);

  const all = useMemo<Array<{ drone: Drone; mine: boolean }>>(() => [
    ...presets.map((d) => ({ drone: d, mine: false })),
    ...mine.map((r) => ({ drone: r.drone, mine: true })),
  ], [presets, mine]);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? all.filter(({ drone }) => `${drone.name} ${drone.description} ${drone.family}`.toLowerCase().includes(t)) : all;
  }, [all, q]);
  const nameOf = (id: string) => all.find((x) => x.drone.id === id)?.drone.name ?? id;
  const currentId = droneIds[activeSlot] ?? droneIds[0];
  const { playingId, toggle: togglePreview, stop: stopPreview } = usePreview();
  // open with the selected drone in view
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    el?.scrollIntoView({ block: 'center' });
  }, [activeSlot]);

  return (
    <div className="glass popover" role="dialog" aria-label="Choose drones">
      <div className="row">
        <div className="seg" role="group" aria-label="Assignment">
          {ASSIGNMENTS.map(([k, label]) => (
            <button key={k} type="button" aria-pressed={assignment === k} onClick={() => { set({ assignment: k }); setSlot(0); }}>{label}</button>
          ))}
        </div>
        <span className="grow" />
        <button type="button" className="btn btn--sm btn--ghost" onClick={onClose} aria-label="Close">✕</button>
      </div>
      {slots.length > 1 && (
        <div className="stack" style={{ gap: 4 }}>
          {slots.map((label, i) => (
            <div key={label} className="slot-row">
              <span>{label}</span>
              <button type="button" className="chip" aria-pressed={activeSlot === i} onClick={() => setSlot(i)}>
                <FamilyDot family={all.find((x) => x.drone.id === (droneIds[i] ?? droneIds[0]))?.drone.family ?? 'white'} />
                {nameOf(droneIds[i] ?? droneIds[0] ?? 'sa')}
                <small>{activeSlot === i ? 'choosing…' : 'change'}</small>
              </button>
            </div>
          ))}
        </div>
      )}
      <input className="input" type="search" placeholder="Search drones" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search drones" />
      <div className="drone-list" role="listbox" aria-label="Drones" ref={listRef}>
        {filtered.map(({ drone, mine: isMine }) => (
          <div key={drone.id} className={`drone-row${drone.id === currentId ? ' is-current' : ''}`}>
            <button type="button" role="option" className="drone-item" aria-selected={drone.id === currentId} aria-pressed={drone.id === currentId}
              onClick={() => { stopPreview(); if (assignment === 'single') set({ droneIds: [drone.id] }); else setDroneSlot(activeSlot, drone.id); }}>
              <FamilyDot family={drone.family} />
              <span className="grow">
                {drone.name}
                <small>{isMine ? 'mine · ' : ''}{drone.description || drone.sources.map((s) => s.kind).join(' + ')}</small>
              </span>
            </button>
            <button type="button" className={`btn btn--sm btn--ghost preview-btn${playingId === drone.id ? ' is-playing' : ''}`}
              aria-pressed={playingId === drone.id}
              title={drone.sources[0]?.kind === 'sample' ? 'Play the original sample' : 'Play this drone at Sa'}
              aria-label={`${playingId === drone.id ? 'Stop' : 'Play'} ${drone.name}`}
              onClick={(e) => { e.stopPropagation(); togglePreview(drone); }}>{playingId === drone.id ? '■' : '▶'}</button>
          </div>
        ))}
        {filtered.length === 0 && <div className="hint" style={{ padding: 10 }}>Nothing matches.</div>}
      </div>
      <div className="row">
        <Link to="/drones" className="btn btn--sm">Manage drones</Link>
        <Link to="/drones/new" className="btn btn--sm btn--ghost">New drone</Link>
      </div>
    </div>
  );
}
